import React, { useState } from "react";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { contact, brand } from "../mock";

const empty = { name: "", phone: "", email: "", company: "", message: "" };

export default function ContactSection() {
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast({ title: "A few details missing", description: "Please add your name, phone and email." });
      return;
    }
    setSubmitting(true);
    // Frontend-only mock: persist to localStorage
    setTimeout(() => {
      const prev = JSON.parse(localStorage.getItem("dye_leads") || "[]");
      prev.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("dye_leads", JSON.stringify(prev));
      setSubmitting(false);
      setForm(empty);
      toast({
        title: "Request received",
        description: "Thank you. Your enquiry is saved locally for now. Backend delivery is coming next.",
      });
    }, 700);
  };

  const field =
    "w-full bg-white border border-[var(--line)] rounded-xl px-4 py-3.5 text-[var(--ivory)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

  return (
    <section id="contact" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <p className="eyebrow">{contact.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-6xl font-semibold leading-[1.02]">
              {contact.title}
            </h2>
            <p className="mt-6 text-[var(--muted)] leading-relaxed max-w-md">{contact.desc}</p>

            <div className="mt-10 space-y-4">
              <a
                href={`tel:${brand.phoneRaw}`}
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 rounded-full border border-[var(--line)] flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <Phone size={18} className="text-[var(--gold)]" />
                </span>
                <span className="text-[var(--ivory)]/85 group-hover:text-[var(--gold)] transition-colors">
                  {brand.phone}
                </span>
              </a>
              <a
                href={`https://wa.me/${brand.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
              >
                <span className="w-11 h-11 rounded-full border border-[var(--line)] flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <MessageCircle size={18} className="text-[var(--gold)]" />
                </span>
                <span className="text-[var(--ivory)]/85 group-hover:text-[var(--gold)] transition-colors">
                  Chat on WhatsApp
                </span>
              </a>
            </div>
          </div>

          <div className="reveal">
            <form
              onSubmit={onSubmit}
              className="rounded-3xl border border-[var(--line)] bg-[var(--ink-2)] p-7 sm:p-9"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input name="name" value={form.name} onChange={onChange} placeholder="Full name *" className={field} />
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone *" className={field} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <input name="email" value={form.email} onChange={onChange} placeholder="Email *" className={field} />
                <input name="company" value={form.company} onChange={onChange} placeholder="Company" className={field} />
              </div>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Tell me about your export goals"
                rows={4}
                className={`${field} mt-4 resize-none`}
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full mt-6 px-7 py-3.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Request My Strategy Call"}
                {!submitting && <ArrowRight size={17} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
